/**
 * Hook useContractsPaginated - Paginação server-side para listagem de contratos gerados
 * Utiliza RPC get_contracts_paginated com offset-based pagination
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-20 v1.0.0 - Criação inicial
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('ContractsPaginated');

/**
 * Campos permitidos para ordenação (whitelist segura)
 */
export type ContractSortField =
  | 'created_at'
  // v1.1.0: ordenação por data de alteração (padrão da listagem)
  | 'updated_at'
  | 'sent_at'
  | 'signed_at'
  | 'status'
  | 'contract_number'
  | 'deal_title'
  | 'deal_value'
  | 'contact_name'
  | 'seller_name'
  | 'assigned_user_name';

/**
 * Direção de ordenação
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Filtros disponíveis para contratos
 */
export interface ContractFilters {
  workspace_id?: string;
  status?: string;
  search?: string;
  seller_id?: string;
  assigned_to?: string;
  date_field?: string;
  date_from?: string;
  date_to?: string;
  signature_provider_slug?: string;
  signer_role?: string;
  published_contract_id?: string;
  template_version_id?: string;
  pending_signer_role?: string;
  tag_ids?: string[];
}

/**
 * Interface do contrato retornado pela RPC get_contracts_paginated
 * Estrutura flat otimizada para tabelas
 */
export interface PaginatedContract {
  id: string;
  title: string;
  status: string;
  contract_number: number | null;
  created_at: string;
  /** v1.1.0: data da última alteração do contrato (mudança de status, PDF, etc.) */
  updated_at: string;
  generated_at: string | null;
  sent_at: string | null;
  signed_at: string | null;
  // Template
  template_name: string | null;
  template_category: string | null;
  version_number: number | null;
  // Deal
  deal_id: string | null;
  deal_title: string | null;
  deal_value: number | null;
  deal_currency: string | null;
  deal_status: string | null;
  // Contato
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  contact_document: string | null;
  contact_avatar: string | null;
  // Workspace/Pipeline
  workspace_name: string | null;
  pipeline_name: string | null;
  // Vendedor
  seller_id: string | null;
  seller_name: string | null;
  seller_avatar: string | null;
  // Responsável
  assigned_to: string | null;
  assigned_user_name: string | null;
  assigned_user_avatar: string | null;
  // Provider assinatura
  provider_slug: string | null;
  provider_display_name: string | null;
  // Assinaturas
  signers_count: number;
  signed_count: number;
  // Contrato publicado
  published_contract_name: string | null;
  // v1.0.5: Campos para ações rápidas na listagem
  pdf_storage_path: string | null;
  queue_error: string | null;
  external_url: string | null;
  // Paginação
  total_count: number;
}

/**
 * Parâmetros de paginação e ordenação
 */
interface PaginationParams {
  page: number;
  pageSize: number;
  sortField?: ContractSortField;
  sortDirection?: SortDirection;
}

/**
 * Resultado da query paginada
 */
interface PaginatedContractsResult {
  contracts: PaginatedContract[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook para buscar contratos com paginação server-side
 * Utiliza RPC get_contracts_paginated para performance em grande escala
 * 
 * @param filters - Filtros para a busca (workspace, status, etc.)
 * @param pagination - Parâmetros de paginação (page, pageSize, sort)
 */
export function useContractsPaginated(
  filters: ContractFilters | undefined,
  pagination: PaginationParams
): PaginatedContractsResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    // Query key inclui filtros e paginação para cache correto
    queryKey: ['contracts-paginated', currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando contratos paginados:', { filters, pagination });

      // Chama RPC com todos os parâmetros
      const { data, error } = await supabase.rpc('get_contracts_paginated', {
        p_page: pagination.page,
        p_page_size: pagination.pageSize,
        p_tenant_hint: currentTenant?.id || null,
        p_workspace_id: filters?.workspace_id || null,
        p_status: filters?.status || null,
        p_search: filters?.search || null,
        p_seller_id: filters?.seller_id || null,
        p_assigned_to: filters?.assigned_to || null,
        p_date_field: filters?.date_field || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        p_signature_provider_slug: filters?.signature_provider_slug || null,
        p_signer_role: filters?.signer_role || null,
        p_published_contract_id: filters?.published_contract_id || null,
        p_template_version_id: filters?.template_version_id || null,
        p_pending_signer_role: filters?.pending_signer_role || null,
        p_tag_ids: filters?.tag_ids || null,
        p_sort_field: pagination.sortField || 'created_at',
        p_sort_direction: pagination.sortDirection || 'desc',
      } as any);

      if (error) {
        logger.error('Erro ao buscar contratos paginados:', error);
        throw error;
      }

      // Total count vem em cada row (pattern COUNT(*) OVER())
      const rawContracts = (data || []) as PaginatedContract[];
      const totalCount = rawContracts[0]?.total_count ?? 0;

      logger.debug(`Recebidos ${rawContracts.length} contratos, total: ${totalCount}`);

      return {
        contracts: rawContracts,
        totalCount: Number(totalCount),
        totalPages: Math.ceil(Number(totalCount) / pagination.pageSize) || 1,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    contracts: query.data?.contracts || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['contracts-paginated', currentTenant?.id]
      });
    },
  };
}
